import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import styles from "./TopBar.module.css";
import coinLogo from "../../asset/icons/fleek-coin-logo.png";
import Api from "../../utils/Api/Api";
import { hasSession } from "../AuthModal/AuthGate";
import { isIndia } from "../../utils/market";
import Helper from "../../utils/Helper";
import { MarketSwitcher } from "../MarketSwitcher/MarketSwitcher";

let cachedBalance = null;

/**
 * Reads the wallet once per page load and shares it across every bar on the
 * page. The count field is not documented, so the first of several likely
 * names is used; the rupee value is the fallback so the pill is never blank
 * for a signed-in customer.
 */
export const useCoinBalance = () => {
  const [balance, setBalance] = useState(cachedBalance);

  useEffect(() => {
    if (cachedBalance !== null || !hasSession()) return;
    let cancelled = false;
    Api.GetFleekCoins()
      .then(({ data }) => {
        if (cancelled || !data) return;
        const count =
          data.coins ?? data.total_coins ?? data.coin_balance ?? data.balance ?? null;
        const value = count ?? data.coin_value_in_inr ?? 0;
        cachedBalance = Number(value) || 0;
        setBalance(cachedBalance);
      })
      .catch(() => {
        cachedBalance = 0;
        if (!cancelled) setBalance(0);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return balance;
};

/**
 * The frame header every purchase-flow screen sits under, from
 * design/Purchase Flow.dc.html: circular back button, screen title, and the
 * coin-balance pill.
 */
export const TopBar = ({ title, onBack, backTo }) => {
  const navigate = useNavigate();
  const balance = useCoinBalance();

  const goBack = () => {
    if (onBack) return onBack();
    if (backTo) return navigate(backTo);
    if (window.history.length > 1) return navigate(-1);
    navigate("/marketplace");
  };

  return (
    <header className={styles.bar}>
      <button type="button" className={styles.back} onClick={goBack} aria-label="Go back">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>
      <h1 className={styles.title}>{title}</h1>
      {/* The store chip; on desktop the NavBar above already carries it. */}
      {Helper.isMobile() && <MarketSwitcher variant="chip" />}
      {/* Coins are rupees: India's store only. */}
      {/* Coins run in India; elsewhere the pill says they're coming. */}
      {isIndia() ? (
        <div className={styles.coins} aria-label="Fleek coin balance">
          <img src={coinLogo} alt="" className={styles.coinsIcon} />
          <span className={styles.coinsValue}>
            {!hasSession() || balance === null ? "—" : Number(balance).toLocaleString("en-IN")}
          </span>
        </div>
      ) : (
        <div className={styles.coins} aria-label="Fleek coins: coming soon" title="Fleek coins are coming soon to your store">
          <img src={coinLogo} alt="" className={styles.coinsIcon} />
          <span className={styles.coinsValue}>Soon</span>
        </div>
      )}
    </header>
  );
};

export default TopBar;
